import { Columns, GridFour, ImageSquare, Ruler, Wall } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import {
  clampFloorSize,
  formatFloorMaxLabel,
  getFloorSizeLimits,
  ROOM_SIZE_LIMITS,
  type RoomSizePlan,
} from '../data/roomLimits';
import { getFloorBlanket, getRoomPolygon, getRoomWalls, makeRoomPolygon, roomBounds } from '../lib/roomGeometry';
import type { FloorBlanketConfig, RoomConfig, RoomPoint, WallDecorationConfig, WallMaterial, WallPillarSpacing, WallSegmentConfig } from '../types';
import { Modal } from './Modal';

type RoomTab = 'size' | 'walls' | 'floor';
type FloorMaterial = NonNullable<RoomConfig['floorMaterial']>;

const wallPresets: { id: Exclude<WallMaterial, 'custom'>; name: string }[] = [
  { id: 'white-concrete', name: '화이트 콘크리트' },
  { id: 'warm-paint', name: '웜 화이트 도장' },
  { id: 'raw-concrete', name: '노출 콘크리트' },
];
const floorPresets: { id: Exclude<FloorMaterial, 'custom'>; name: string }[] = [
  { id: 'light-concrete', name: '라이트 콘크리트' },
  { id: 'dark-concrete', name: '다크 콘크리트' },
  { id: 'oak', name: '내추럴 오크' },
  { id: 'carpet', name: '차콜 카펫' },
];
const floorColors: Record<Exclude<FloorMaterial, 'custom'>, string> = {
  'light-concrete': '#d8dad5',
  'dark-concrete': '#4f5652',
  oak: '#b89468',
  carpet: '#454c49',
};
const blanketPresets: { id: Exclude<FloorBlanketConfig['material'], 'custom'>; name: string; color: string }[] = [
  { id: 'gray-felt', name: '그레이 모포', color: '#8a9090' },
  { id: 'charcoal', name: '차콜 모포', color: '#5c6360' },
  { id: 'sand', name: '샌드 모포', color: '#b8b0a4' },
];
const blanketStyles: { id: FloorBlanketConfig['style']; name: string }[] = [
  { id: 'plain', name: '무지' },
  { id: 'woven', name: '직조' },
  { id: 'ribbed', name: '줄무늬' },
];
const decorationColors = ['#e07a3a', '#b26748', '#33474a', '#53634e', '#272b2a', '#f3f1e8'];
const pillarSpacingOptions: { id: WallPillarSpacing; name: string }[] = [
  { id: 0, name: '없음' },
  { id: 1, name: '1M' },
  { id: 2, name: '2M' },
  { id: 3, name: '3M' },
];
const materialColors: Record<WallMaterial, string> = {
  'white-concrete': '#faf9f4',
  'warm-paint': '#f3ecdf',
  'raw-concrete': '#858b87',
  custom: '#b26748',
};

async function prepareSurfaceImage(file: File) {
  const source = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const next = new Image();
    next.onload = () => resolve(next);
    next.onerror = reject;
    next.src = source;
  });
  const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) return source;
  context.fillStyle = '#f4f3ee';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.84);
}

function normalizeRoom(room: RoomConfig): RoomConfig {
  const polygon = getRoomPolygon(room);
  return {
    ...room,
    shapeSides: polygon.length as 4 | 5 | 6 | 7,
    polygon,
    wallSegments: getRoomWalls(room),
    floorBlanket: getFloorBlanket(room),
  };
}

function PolygonEditor({ points, onChange }: { points: RoomPoint[]; onChange: (points: RoomPoint[]) => void }) {
  const [dragging, setDragging] = useState<number | null>(null);
  const range = Math.max(10, ...points.flatMap((point) => [Math.abs(point.x) * 2.35, Math.abs(point.z) * 2.35]));
  const scale = 148 / range;
  const toScreen = (point: RoomPoint) => ({ x: 210 + point.x * scale, y: 90 + point.z * scale });
  const polygonPoints = points.map((point) => `${toScreen(point).x},${toScreen(point).y}`).join(' ');

  const updatePoint = (event: React.PointerEvent<SVGSVGElement>) => {
    if (dragging === null) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (((event.clientX - rect.left) / rect.width) * 420 - 210) / scale;
    const z = (((event.clientY - rect.top) / rect.height) * 180 - 90) / scale;
    const limit = range / 2.05;
    const next = points.map((point, index) => index === dragging
      ? { x: Number(Math.max(-limit, Math.min(limit, x)).toFixed(2)), z: Number(Math.max(-limit, Math.min(limit, z)).toFixed(2)) }
      : point);
    onChange(next);
  };

  return (
    <svg
      className="polygon-editor"
      viewBox="0 0 420 180"
      role="img"
      aria-label={`${points.length}각형 바닥 꼭짓점 편집기`}
      onPointerMove={updatePoint}
      onPointerUp={() => setDragging(null)}
      onPointerCancel={() => setDragging(null)}
    >
      <defs><pattern id="editor-grid" width="18" height="18" patternUnits="userSpaceOnUse"><path d="M 18 0 L 0 0 0 18" fill="none" stroke="currentColor" strokeWidth="0.45" /></pattern></defs>
      <rect width="420" height="180" fill="url(#editor-grid)" className="polygon-grid" />
      <polygon points={polygonPoints} className="polygon-floor-shape" />
      {points.map((point, index) => {
        const screen = toScreen(point);
        return (
          <g key={index} transform={`translate(${screen.x} ${screen.y})`}>
            <circle
              r="10"
              className={`polygon-handle ${dragging === index ? 'active' : ''}`}
              onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); setDragging(index); }}
            />
            <text y="3.5">{index + 1}</text>
          </g>
        );
      })}
    </svg>
  );
}

function WallSelector({ points, walls, selected, onSelect }: { points: RoomPoint[]; walls: WallSegmentConfig[]; selected: number; onSelect: (index: number) => void }) {
  const range = Math.max(10, ...points.flatMap((point) => [Math.abs(point.x) * 2.4, Math.abs(point.z) * 2.4]));
  // 선택 평면도는 꼭짓점과 벽을 직접 누르는 조작 영역이므로 편집기보다 크게 표시한다.
  const scale = 290 / range;
  const toScreen = (point: RoomPoint) => ({ x: 210 + point.x * scale, y: 90 + point.z * scale });
  const polygonPoints = points.map((point) => `${toScreen(point).x},${toScreen(point).y}`).join(' ');
  return (
    <svg className="wall-selector" viewBox="0 0 420 180" role="img" aria-label={`${walls.length}개 벽 선택 평면도`}>
      <polygon points={polygonPoints} className="wall-selector-floor" />
      {walls.map((wall, index) => {
        const start = toScreen(points[index]);
        const end = toScreen(points[index + 1]);
        const centerX = (start.x + end.x) / 2;
        const centerY = (start.y + end.y) / 2;
        return (
          <g key={index} className={`wall-segment ${selected === index ? 'selected' : ''} ${wall.enabled ? 'enabled' : 'disabled'}`} onClick={() => onSelect(index)}>
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} className="wall-segment-hit" />
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} style={{ '--wall-color': materialColors[wall.material] } as React.CSSProperties} />
            <circle cx={centerX} cy={centerY} r="10" />
            <text x={centerX} y={centerY + 3.5}>{index + 1}</text>
          </g>
        );
      })}
      <line x1={toScreen(points.at(-1)!).x} y1={toScreen(points.at(-1)!).y} x2={toScreen(points[0]).x} y2={toScreen(points[0]).y} className="open-edge" />
      <text x="210" y="169" className="open-edge-label">출입구 변에는 벽이 생성되지 않습니다</text>
    </svg>
  );
}

interface RoomModalProps {
  open: boolean;
  room: RoomConfig;
  /** 문의 승인 후 엔터프라이즈 한도를 적용할 때 'enterprise' */
  sizePlan?: RoomSizePlan;
  onClose: () => void;
  onSave: (room: RoomConfig) => void;
}

function fitPolygonToFloorLimits(polygon: RoomPoint[], plan: RoomSizePlan) {
  const limits = getFloorSizeLimits(plan);
  const bounds = roomBounds(polygon);
  const scaleX = bounds.width > limits.maxWidth ? limits.maxWidth / bounds.width : 1;
  const scaleZ = bounds.depth > limits.maxDepth ? limits.maxDepth / bounds.depth : 1;
  const scale = Math.min(scaleX, scaleZ);
  if (scale >= 1) {
    return {
      polygon,
      width: Number(bounds.width.toFixed(2)),
      depth: Number(bounds.depth.toFixed(2)),
    };
  }
  const fitted = polygon.map((point) => ({
    x: Number((point.x * scale).toFixed(2)),
    z: Number((point.z * scale).toFixed(2)),
  }));
  const nextBounds = roomBounds(fitted);
  return {
    polygon: fitted,
    width: Number(nextBounds.width.toFixed(2)),
    depth: Number(nextBounds.depth.toFixed(2)),
  };
}

export function RoomModal({ open, room, sizePlan = 'standard', onClose, onSave }: RoomModalProps) {
  const [draft, setDraft] = useState(() => normalizeRoom(room));
  const [tab, setTab] = useState<RoomTab>('size');
  const [selectedWall, setSelectedWall] = useState(0);
  const floorLimits = useMemo(() => getFloorSizeLimits(sizePlan), [sizePlan]);
  const floorMaxLabel = useMemo(() => formatFloorMaxLabel(sizePlan), [sizePlan]);
  useEffect(() => {
    if (!open) return;
    const normalized = normalizeRoom(room);
    const fitted = fitPolygonToFloorLimits(getRoomPolygon(normalized), sizePlan);
    setDraft({ ...normalized, ...fitted });
    setTab('size');
    setSelectedWall(0);
  }, [open, room, sizePlan]);

  const points = draft.polygon ?? getRoomPolygon(draft);
  const walls = draft.wallSegments ?? getRoomWalls(draft);
  const selectedWallConfig = walls[selectedWall] ?? walls[0];
  const bounds = useMemo(() => roomBounds(points), [points]);

  const changeSides = (sides: 4 | 5 | 6 | 7) => {
    const clamped = clampFloorSize(draft.width, draft.depth, sizePlan);
    const polygon = makeRoomPolygon(sides, clamped.width, clamped.depth);
    const previousWalls = walls;
    const wallSegments = Array.from({ length: sides - 1 }, (_, index) => previousWalls[index] ?? {
      enabled: index < 2,
      style: 'solid' as const,
      material: 'white-concrete' as const,
    });
    setDraft({ ...draft, ...clamped, shapeSides: sides, polygon, wallSegments });
    setSelectedWall(Math.min(selectedWall, sides - 2));
  };
  const updatePolygon = (polygon: RoomPoint[]) => {
    const fitted = fitPolygonToFloorLimits(polygon, sizePlan);
    setDraft({ ...draft, ...fitted });
  };
  const updateRectangleDimension = (field: 'width' | 'depth' | 'height', value: number) => {
    if (field === 'height') {
      const height = Math.max(ROOM_SIZE_LIMITS.minHeight, Math.min(ROOM_SIZE_LIMITS.maxHeight, value));
      setDraft({ ...draft, height });
      return;
    }
    const next = clampFloorSize(
      field === 'width' ? value : draft.width,
      field === 'depth' ? value : draft.depth,
      sizePlan,
    );
    setDraft({ ...draft, ...next, polygon: makeRoomPolygon(4, next.width, next.depth) });
  };
  const updateWall = (patch: Partial<WallSegmentConfig>) => {
    const wallSegments = walls.map((wall, index) => index === selectedWall ? { ...wall, ...patch } : wall);
    setDraft({ ...draft, wallSegments });
  };
  const setWallImage = async (file?: File) => {
    if (!file) return;
    updateWall({ material: 'custom', imageUrl: await prepareSurfaceImage(file), imageName: file.name });
  };
  const setFloorImage = async (file?: File) => {
    if (!file) return;
    setDraft({ ...draft, floorMaterial: 'custom', floorImageUrl: await prepareSurfaceImage(file), floorImageName: file.name });
  };
  const setBlanketImage = async (file?: File) => {
    if (!file) return;
    const blanket = getFloorBlanket(draft);
    setDraft({
      ...draft,
      floorBlanket: {
        ...blanket,
        material: 'custom',
        imageUrl: await prepareSurfaceImage(file),
        imageName: file.name,
      },
    });
  };
  const updateBlanket = (patch: Partial<FloorBlanketConfig>) => {
    const blanket = getFloorBlanket(draft);
    setDraft({ ...draft, floorBlanket: { ...blanket, ...patch } });
  };
  const updateDecoration = (patch: Partial<WallDecorationConfig>) => {
    const decoration = selectedWallConfig.decoration ?? { type: 'none', color: '#e07a3a', height: 0.35, offsetY: 1.35 };
    updateWall({ decoration: { ...decoration, ...patch } });
  };
  const blanket = getFloorBlanket(draft);

  return (
    <Modal open={open} onClose={onClose} title="공간 설정" description="바닥 형태를 만들고 변마다 벽의 구조와 마감재를 지정하세요." className="room-modal room-material-modal">
      <div className="room-settings-tabs" role="tablist" aria-label="공간 설정 구분">
        <button type="button" role="tab" aria-selected={tab === 'size'} className={tab === 'size' ? 'active' : ''} onClick={() => setTab('size')}><Ruler size={17} />바닥 형태</button>
        <button type="button" role="tab" aria-selected={tab === 'walls'} className={tab === 'walls' ? 'active' : ''} onClick={() => setTab('walls')}><Wall size={17} />벽면</button>
        <button type="button" role="tab" aria-selected={tab === 'floor'} className={tab === 'floor' ? 'active' : ''} onClick={() => setTab('floor')}><GridFour size={17} />바닥 마감</button>
      </div>

      {tab === 'size' && (
        <div className="room-tab-panel polygon-room-panel">
          <div className="polygon-shape-toolbar">
            <div><strong>바닥면 변 개수</strong><small>4각형부터 7각형까지 지원합니다.</small></div>
            <div role="radiogroup" aria-label="바닥면 변 개수">
              {([4, 5, 6, 7] as const).map((sides) => <button key={sides} type="button" role="radio" aria-checked={points.length === sides} className={points.length === sides ? 'active' : ''} onClick={() => changeSides(sides)}>{sides}각형</button>)}
            </div>
          </div>
          <PolygonEditor points={points} onChange={updatePolygon} />
          <div className="polygon-editor-help"><span>꼭짓점을 드래그해 비정형 바닥을 만드세요.</span><strong>{bounds.width.toFixed(1)} × {bounds.depth.toFixed(1)} m</strong></div>
          {points.length === 4 ? (
            <div className="form-grid compact-dimensions">
              <label><span>너비 (X)</span><div><input type="number" min={floorLimits.minWidth} max={floorLimits.maxWidth} step="0.1" value={draft.width} onChange={(event) => updateRectangleDimension('width', Number(event.target.value))} /><em>m</em></div></label>
              <label><span>깊이 (Z)</span><div><input type="number" min={floorLimits.minDepth} max={floorLimits.maxDepth} step="0.1" value={draft.depth} onChange={(event) => updateRectangleDimension('depth', Number(event.target.value))} /><em>m</em></div></label>
              <label><span>벽 높이</span><div><input type="number" min={ROOM_SIZE_LIMITS.minHeight} max={ROOM_SIZE_LIMITS.maxHeight} step="0.1" value={draft.height} onChange={(event) => updateRectangleDimension('height', Number(event.target.value))} /><em>m</em></div></label>
            </div>
          ) : <p className="polygon-dimension-note">비정형 공간의 크기는 꼭짓점 이동으로 조절합니다. 벽 높이는 현재 {draft.height}m를 유지합니다.</p>}
          <p className="floor-size-limit-note">
            최대 <strong>{floorMaxLabel}</strong>까지 설정할 수 있습니다.
            {sizePlan === 'standard' && (
              <> 이보다 큰 행사장 급 공간이 필요하면 <a href={ROOM_SIZE_LIMITS.inquiryUrl} target="_blank" rel="noreferrer">문의해 주세요</a>.</>
            )}
          </p>
        </div>
      )}

      {tab === 'walls' && selectedWallConfig && (
        <div className="room-tab-panel polygon-walls-panel">
          <div className="wall-plan-wrap">
            <div className="wall-plan-heading"><div><strong>벽 선택 평면도</strong><small>색이 있는 변을 클릭해 아래에서 설정하세요.</small></div><span>{walls.filter((wall) => wall.enabled).length}/{walls.length}개 사용</span></div>
            <WallSelector points={points} walls={walls} selected={selectedWall} onSelect={setSelectedWall} />
          </div>
          <section className="selected-wall-editor">
            <header>
              <div><strong>벽 {selectedWall + 1}</strong><small>꼭짓점 {selectedWall + 1}–{selectedWall + 2} 구간</small></div>
              <label className="surface-toggle"><input type="checkbox" checked={selectedWallConfig.enabled} onChange={(event) => updateWall({ enabled: event.target.checked })} /><span>{selectedWallConfig.enabled ? '표시' : '제거'}</span></label>
            </header>
            <div className="wall-style-options" role="radiogroup" aria-label="벽 스타일">
              <button type="button" role="radio" aria-checked={selectedWallConfig.style === 'solid'} className={selectedWallConfig.style === 'solid' ? 'active' : ''} onClick={() => updateWall({ style: 'solid' })}><Wall size={22} /><span><strong>단순 벽</strong><small>두께가 있는 일반 가벽</small></span></button>
              <button type="button" role="radio" aria-checked={selectedWallConfig.style === 'expo-panel'} className={selectedWallConfig.style === 'expo-panel' ? 'active' : ''} onClick={() => updateWall({ style: 'expo-panel' })}><Columns size={22} /><span><strong>부스 가벽</strong><small>원형 쇠봉과 얇은 패널</small></span></button>
            </div>
            <div className="surface-preset-grid selected-wall-materials">
              {wallPresets.map((preset) => (
                <button key={preset.id} type="button" className={selectedWallConfig.material === preset.id ? 'active' : ''} onClick={() => updateWall({ material: preset.id })}>
                  <i className={`surface-swatch wall-${preset.id}`} /><span>{preset.name}</span>
                </button>
              ))}
              <label className={`surface-upload ${selectedWallConfig.material === 'custom' ? 'active' : ''}`}>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setWallImage(event.target.files?.[0])} />
                <i style={selectedWallConfig.imageUrl ? { backgroundImage: `url(${selectedWallConfig.imageUrl})` } : undefined}><ImageSquare size={18} /></i>
                <span>{selectedWallConfig.imageName ?? '내 이미지'}</span>
              </label>
            </div>
            {selectedWallConfig.style === 'expo-panel' && (
              <section className="wall-pillar-editor">
                <header><strong>대(기둥) 간격</strong><small>부스 가벽 지지대 배치</small></header>
                <div className="pillar-spacing-options" role="radiogroup" aria-label="대 간격">
                  {pillarSpacingOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      role="radio"
                      aria-checked={(selectedWallConfig.pillarSpacing ?? 1) === option.id}
                      className={(selectedWallConfig.pillarSpacing ?? 1) === option.id ? 'active' : ''}
                      onClick={() => updateWall({ pillarSpacing: option.id })}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </section>
            )}
            <section className="wall-decoration-editor">
              <header><strong>벽 데코레이션</strong><small>가로 띠 등 전시 연출 요소</small></header>
              <div className="decoration-type-options" role="radiogroup" aria-label="벽 데코레이션">
                <button type="button" role="radio" aria-checked={(selectedWallConfig.decoration?.type ?? 'none') === 'none'} className={(selectedWallConfig.decoration?.type ?? 'none') === 'none' ? 'active' : ''} onClick={() => updateDecoration({ type: 'none' })}>없음</button>
                <button type="button" role="radio" aria-checked={selectedWallConfig.decoration?.type === 'stripe'} className={selectedWallConfig.decoration?.type === 'stripe' ? 'active' : ''} onClick={() => updateDecoration({ type: 'stripe' })}>가로 띠</button>
              </div>
              {selectedWallConfig.decoration?.type === 'stripe' && (
                <div className="decoration-stripe-controls">
                  <div className="color-options decoration-colors">
                    {decorationColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={(selectedWallConfig.decoration?.color ?? '#e07a3a') === color ? 'active' : ''}
                        style={{ '--swatch': color } as React.CSSProperties}
                        onClick={() => updateDecoration({ color })}
                        aria-label={`데코 색상 ${color}`}
                      />
                    ))}
                  </div>
                  <div className="form-grid compact-dimensions">
                    <label><span>띠 높이</span><div><input type="number" min={0.1} max={1.2} step="0.05" value={selectedWallConfig.decoration?.height ?? 0.35} onChange={(event) => updateDecoration({ height: Number(event.target.value) })} /><em>m</em></div></label>
                    <label><span>중심 높이</span><div><input type="number" min={0.2} max={3} step="0.05" value={selectedWallConfig.decoration?.offsetY ?? 1.35} onChange={(event) => updateDecoration({ offsetY: Number(event.target.value) })} /><em>m</em></div></label>
                  </div>
                </div>
              )}
            </section>
          </section>
        </div>
      )}

      {tab === 'floor' && (
        <div className="room-tab-panel surface-panel">
          <section className="surface-editor floor-editor">
            <header><div><strong>바닥 마감</strong><small>다각형 바닥 전체에 패턴이 자동 반복됩니다.</small></div></header>
            <div className="surface-preset-grid floor-preset-grid">
              {floorPresets.map((preset) => (
                <button key={preset.id} type="button" className={(draft.floorMaterial ?? 'light-concrete') === preset.id ? 'active' : ''} onClick={() => setDraft({ ...draft, floorMaterial: preset.id, floorColor: floorColors[preset.id], floorImageUrl: undefined, floorImageName: undefined })}>
                  <i className={`surface-swatch floor-${preset.id}`} /><span>{preset.name}</span>
                </button>
              ))}
              <label className={`surface-upload ${draft.floorMaterial === 'custom' ? 'active' : ''}`}>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setFloorImage(event.target.files?.[0])} />
                <i style={draft.floorImageUrl ? { backgroundImage: `url(${draft.floorImageUrl})` } : undefined}><ImageSquare size={18} /></i>
                <span>{draft.floorImageName ?? '내 이미지'}</span>
              </label>
            </div>
          </section>
          <section className="surface-editor blanket-editor">
            <header>
              <div><strong>바닥 모포</strong><small>부스 바닥 위에 깔리는 전시용 카펫/모포</small></div>
              <label className="surface-toggle"><input type="checkbox" checked={blanket.enabled} onChange={(event) => updateBlanket({ enabled: event.target.checked })} /><span>{blanket.enabled ? '사용' : '제거'}</span></label>
            </header>
            {blanket.enabled && (
              <>
                <div className="surface-preset-grid blanket-preset-grid">
                  {blanketPresets.map((preset) => (
                    <button key={preset.id} type="button" className={blanket.material === preset.id ? 'active' : ''} onClick={() => updateBlanket({ material: preset.id, color: preset.color, imageUrl: undefined, imageName: undefined })}>
                      <i className={`surface-swatch blanket-${preset.id}`} /><span>{preset.name}</span>
                    </button>
                  ))}
                  <label className={`surface-upload ${blanket.material === 'custom' ? 'active' : ''}`}>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setBlanketImage(event.target.files?.[0])} />
                    <i style={blanket.imageUrl ? { backgroundImage: `url(${blanket.imageUrl})` } : undefined}><ImageSquare size={18} /></i>
                    <span>{blanket.imageName ?? '내 이미지'}</span>
                  </label>
                </div>
                <div className="blanket-style-options" role="radiogroup" aria-label="모포 스타일">
                  {blanketStyles.map((style) => (
                    <button key={style.id} type="button" role="radio" aria-checked={blanket.style === style.id} className={blanket.style === style.id ? 'active' : ''} onClick={() => updateBlanket({ style: style.id })}>{style.name}</button>
                  ))}
                </div>
                <label className="blanket-color-field">
                  <span>모포 색상</span>
                  <input type="color" value={blanket.color} onChange={(event) => updateBlanket({ color: event.target.value })} />
                </label>
              </>
            )}
          </section>
        </div>
      )}

      <div className="modal-actions"><button className="button button-secondary" type="button" onClick={onClose}>취소</button><button className="button button-primary" type="button" onClick={() => { onSave(draft); onClose(); }}>공간에 적용</button></div>
    </Modal>
  );
}
